const INVITE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const INVITE_BODY_LENGTH = 16;
const INVITE_PATTERN = /^LEITOUR-[A-HJ-NP-Z2-9]{4}(?:-[A-HJ-NP-Z2-9]{4}){3}$/;

declare const inviteCodeHashBrand: unique symbol;
export type InviteCodeHash = string & { readonly [inviteCodeHashBrand]: true };

export class InviteCodeFormatError extends Error {
  constructor() {
    super('Código de convite inválido.');
    this.name = 'InviteCodeFormatError';
  }
}

export const normalizeInviteCode = (value: string): string => {
  const compact = value.trim().toUpperCase().replace(/[\s-]+/g, '');
  if (!compact.startsWith('LEITOUR') || compact.length !== 'LEITOUR'.length + INVITE_BODY_LENGTH) {
    return value.trim().toUpperCase();
  }

  const body = compact.slice('LEITOUR'.length);
  return `LEITOUR-${body.match(/.{1,4}/g)?.join('-') ?? body}`;
};

export const isValidInviteCode = (value: string): boolean => INVITE_PATTERN.test(normalizeInviteCode(value));

const secureRandomBytes = (): Uint8Array => {
  const bytes = new Uint8Array(INVITE_BODY_LENGTH);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
};

export const generateInviteCode = (randomBytes: () => Uint8Array = secureRandomBytes): string => {
  const bytes = randomBytes();
  if (bytes.length < INVITE_BODY_LENGTH) throw new Error('A fonte aleatória não forneceu bytes suficientes.');

  // O alfabeto possui 32 símbolos; 256 é divisível por 32 e evita viés de módulo.
  const body = Array.from(bytes.slice(0, INVITE_BODY_LENGTH), (byte) => INVITE_ALPHABET[byte % INVITE_ALPHABET.length]).join('');
  return `LEITOUR-${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}-${body.slice(12, 16)}`;
};

export const hashInviteCode = async (value: string): Promise<InviteCodeHash> => {
  const normalized = normalizeInviteCode(value);
  if (!INVITE_PATTERN.test(normalized)) throw new InviteCodeFormatError();

  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('') as InviteCodeHash;
};
